import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";
import { fmtIDR } from "@/lib/format";

const JurnalApproval = () => {
  const [rows, setRows] = useState([]);
  const [coa, setCoa] = useState([]);
  const [tab, setTab] = useState("pending");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get(`/journals?status=${tab}`).then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/coa").then((r) => setCoa(r.data)); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (id) => {
    try { await api.post(`/journals/${id}/approve`); toast.success("Jurnal disetujui"); load(); }
    catch (e) { toast.error("Gagal menyetujui"); }
  };
  const reject = async () => {
    try {
      await api.post(`/journals/${rejecting}/reject`, { reason });
      toast.success("Jurnal ditolak"); setRejecting(null); setReason(""); load();
    } catch (e) { toast.error("Gagal menolak"); }
  };

  const accountLabel = (code) => {
    const c = coa.find((x) => x.code === code);
    return c ? `${c.code} — ${c.name}` : code;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Approval Jurnal Umum</h1>
        <p className="text-sm text-slate-500 mt-1">Setujui atau tolak jurnal yang diinput operator sebelum masuk buku besar</p>
      </div>

      <div className="flex gap-2">
        {["pending", "approved", "rejected"].map((t) => (
          <Button key={t} variant={tab === t ? "default" : "outline"}
            className={tab === t ? "bg-[#E30613] hover:bg-[#C8040F]" : ""}
            onClick={() => setTab(t)} data-testid={`tab-${t}`}>
            {t === "pending" ? "Menunggu" : t === "approved" ? "Disetujui" : "Ditolak"}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold flex items-center gap-2"><ClipboardCheck className="w-5 h-5" />Daftar Jurnal ({tab})</CardTitle></CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="text-center text-slate-500 py-10">Tidak ada jurnal {tab}</div>
          ) : (
            <div className="space-y-4">
              {rows.map((j) => (
                <div key={j.id} className="border rounded-lg overflow-hidden" data-testid={`approval-row-${j.id}`}>
                  <div className="flex items-center justify-between p-3 bg-slate-50 border-b">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Badge variant="secondary" className="font-mono-fig">{j.date}</Badge>
                      <div className="text-sm font-semibold">{j.description}</div>
                      {j.reference && <Badge variant="outline" className="text-xs">{j.reference}</Badge>}
                      <span className="text-xs text-slate-500">oleh {j.created_by}</span>
                      {j.status === "rejected" && j.rejected_reason && <Badge className="bg-rose-50 text-rose-700 border border-rose-200 text-xs">Alasan: {j.rejected_reason}</Badge>}
                    </div>
                    {j.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approve(j.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${j.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" />Setujui
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setRejecting(j.id)} data-testid={`reject-${j.id}`}>
                          <XCircle className="w-4 h-4 mr-1" />Tolak
                        </Button>
                      </div>
                    )}
                  </div>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Akun</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Kredit</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {j.lines.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>{accountLabel(l.account)}{l.note && <span className="text-slate-400 text-xs ml-2">• {l.note}</span>}</TableCell>
                          <TableCell className="text-right font-mono-fig">{l.debit > 0 ? fmtIDR(l.debit) : ""}</TableCell>
                          <TableCell className="text-right font-mono-fig">{l.credit > 0 ? fmtIDR(l.credit) : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tolak Jurnal</DialogTitle>
            <DialogDescription>Berikan alasan penolakan agar operator dapat memperbaikinya.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Alasan Penolakan</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: akun tidak sesuai" />
          </div>
          <DialogFooter><Button onClick={reject} className="bg-rose-600 hover:bg-rose-700">Konfirmasi Tolak</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JurnalApproval;
